import { DateTime } from 'luxon';
import { createSelector } from 'reselect';

import { datasetsSelectors } from '../datasets';
import { preferencesSelectors } from '../preferences';

import RootState from 'app-components/utils/redux-store/rootState';
import { AUTH_REDUCER_KEY, AuthStoreProps } from './authTypes';

const graceTimeMs = 1000 * 60 * 3; // give 3 minutes gap for token exchange or invalid local time

type State = Pick<RootState, AUTH_REDUCER_KEY>;

export const getAuthSession = (state: State) => state[AUTH_REDUCER_KEY];

export const getIsLoading = createSelector([getAuthSession], (store) => store.isLoading);
export const getError = createSelector([getAuthSession], (store) => store.error);

export const getSource = createSelector([getAuthSession], (store) => store.source);
export const getQCSId = createSelector([getAuthSession], (store) => store.qcsId);

export const isAccessTokenValid = (authSession: Pick<AuthStoreProps, 'accessTokenExpired' | 'accessToken'>) => {
  const now = DateTime.local();
  const accessTokenExpiredGrace = DateTime.fromISO(authSession.accessTokenExpired!).minus(graceTimeMs);
  return authSession.accessToken && accessTokenExpiredGrace > now;
};

export const getAccessToken = (state: State) => {
  let accessToken;

  const authSession = getAuthSession(state);
  if (isAccessTokenValid(authSession)) {
    accessToken = authSession.accessToken;
  }

  return accessToken;
};

export const isRefreshTokenValid = (authSession: AuthStoreProps) => {
  const now = DateTime.local();
  const refreshTokenExpiredGrace = DateTime.fromISO(authSession.refreshTokenExpired!).minus(graceTimeMs);
  return authSession.refreshToken && refreshTokenExpiredGrace > now;
};

export const getRefreshToken = (state: State) => {
  let refreshToken;

  const authSession = getAuthSession(state);
  if (isRefreshTokenValid(authSession)) {
    refreshToken = authSession.refreshToken;
  }

  return refreshToken;
};

export const isAuthTokenValid = (state: State) => {
  const authSession = getAuthSession(state);
  const sanitized = authSession.isSanitized;
  return Boolean(sanitized || isAccessTokenValid(authSession) || isRefreshTokenValid(authSession));
};

export const getDatasetId = createSelector(getAuthSession, (authSession) => authSession.datasetId);
export const getCurrentDataset = createSelector(
  [getDatasetId, datasetsSelectors.getDatasetsById],
  (datasetId, datasets) => (datasetId ? datasets?.get(datasetId) : undefined),
);

export const isCurrentDatasetShared = createSelector([getCurrentDataset], (dataset) => dataset?.shared);

export const getAccessControl = createSelector(getCurrentDataset, (dataset) => dataset?.itemAccessControl);

export const isAuthenticated = isAuthTokenValid;

export const isAuthenticatedWithDataSetId = createSelector(
  [getDatasetId, getCurrentDataset, isAuthenticated],
  (datasetId, dataset, authed) => Boolean(authed && datasetId && (!dataset || !dataset.disableRemoteAccess)),
);

export const isCanadianDataset = createSelector(
  (state, datasetId) =>
    datasetId ? datasetsSelectors.getDatasetsById(state).get(datasetId) : getCurrentDataset(state),
  (dataset) => ['QWIN_CA', 'QWIN_CA_MWF'].includes(dataset?.platform),
);

export const getIsWebfirstDataset = createSelector(
  getCurrentDataset,
  (currentDataset) =>
    currentDataset && currentDataset.platform && ['QWIN_MWF', 'QMAC_MWF'].includes(currentDataset.platform),
);

export const getIsMacDataset = createSelector(getCurrentDataset, (currentDataset) =>
  currentDataset?.platform?.includes('QMAC'),
);

export const getAuthTheme = createSelector(preferencesSelectors.getWebThemes, getDatasetId, (themeColor, datasetId) => {
  const dm = datasetId ? themeColor.current : themeColor.system;
  return dm === 'dark' ? 1 : 0;
});
